import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { supabase } from '@/lib/supabase';
import { signInErrorMessage } from '@/lib/authErrors';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { FlowLogo } from '@/components/ui/FlowLogo';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function Login() {
  const navigate = useNavigate();
  const { signIn, isAuthenticated } = useAuth();
  const { loginWithRedirect, isMsalConfigured, isAuthenticated: isMsAuthenticated } = useMicrosoftAuth();
  const [error, setError] = useState<string | null>(null);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect to dashboard when authenticated (either Supabase or Microsoft)
  // But NOT if we just signed out (to prevent auto-login loop)
  useEffect(() => {
    if (isAuthenticated || isMsAuthenticated) {
      // Only consume the signing-out flag when it actually blocks a redirect.
      // Clearing it on the unauthenticated first render would let a session
      // restored after a FAILED server sign-out ride straight back in.
      if (sessionStorage.getItem('flow_signing_out')) {
        return;
      }
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isMsAuthenticated, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      // A deliberate login supersedes any lingering sign-out guard
      sessionStorage.removeItem('flow_signing_out');
      await signIn(data.email, data.password);
      // A researcher signing in here has no Flow access at all and would land
      // on the "Access Restricted" card. Send them to their own portal instead.
      // check_portal_access() is fail-safe and cheap; anything unexpected falls
      // through to the normal dashboard route.
      try {
        const { data: access } = await supabase.rpc('check_portal_access');
        const a = access as { is_researcher?: boolean; is_active?: boolean } | null;
        if (a?.is_researcher && a?.is_active) {
          navigate('/portal', { replace: true });
          return;
        }
      } catch {
        /* not a researcher, or the RPC is unavailable — carry on to Flow */
      }
      // Explicitly navigate to dashboard after successful sign in
      navigate('/', { replace: true });
    } catch (err) {
      setError(signInErrorMessage(err));
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!isMsalConfigured) {
      setError('Microsoft login is not configured. Please contact your administrator.');
      return;
    }

    try {
      setError(null);
      setIsMicrosoftLoading(true);

      // A deliberate login supersedes any lingering sign-out guard
      sessionStorage.removeItem('flow_signing_out');

      // Use redirect flow (more reliable than popup)
      await loginWithRedirect();

      // Note: The page will redirect, so the code below won't execute
    } catch (err) {
      console.error('Microsoft login error:', err);
      if (err instanceof Error) {
        setError(`Microsoft login failed: ${err.message}`);
      } else {
        setError('Microsoft login failed. Please try again.');
      }
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <FlowLogo height={44} className="mx-auto mb-6" />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Welcome to Flow</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <Card padding="lg">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div role="alert" className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 dark:bg-danger-900/30 dark:border-danger-800 dark:text-danger-300">
                {error}
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="you@elevationcapitalgroup.com"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register('password')}
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isSubmitting}
            >
              Sign in
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-500 dark:bg-gray-900 dark:text-gray-400">Or continue with</span>
            </div>
          </div>

          {/* Microsoft SSO */}
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={handleMicrosoftLogin}
            isLoading={isMicrosoftLoading}
            disabled={isMicrosoftLoading}
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 21 21" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
          </Button>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
          Elevation Capital Group Internal Platform
        </p>
      </div>
    </div>
  );
}
